import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeftIcon, PinIcon } from 'lucide-react';
import { LicenceStatusChip, formatMoney } from '@/components/admin/licence-status-chip';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { adminApi, adminKeys, customerQuery } from '@/lib/api/admin';
import { messageForError } from '@/lib/api-client';
import { formatDate, formatDateTime } from '@/lib/utils';

function CustomerDetailPage() {
  const { organisationId } = Route.useParams();
  const queryClient = useQueryClient();
  const query = useQuery(customerQuery(organisationId));
  const [note, setNote] = useState('');
  const [pinned, setPinned] = useState(false);

  const addNote = useMutation({
    mutationFn: () => adminApi.addNote(organisationId, { body: note.trim(), pinned }),
    onSuccess: () => {
      toast.success('Support note added');
      setNote('');
      setPinned(false);
      void queryClient.invalidateQueries({ queryKey: adminKeys.customer(organisationId) });
    },
    onError: (error) => toast.error(messageForError(error)),
  });

  const back = (
    <Button variant="secondary" size="sm" asChild>
      <Link to="/admin/customers" search={{ page: 1 }}>
        <ArrowLeftIcon /> All organisations
      </Link>
    </Button>
  );

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-16 w-80" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <EmptyState
        title="Customer not found"
        description={messageForError(query.error)}
        action={back}
      />
    );
  }

  const data = query.data;

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Organisation"
        title={data.organisation.name}
        description={`${data.organisation.slug} · created ${formatDate(data.organisation.createdAt)}${
          data.organisation.billingEmail ? ` · billing ${data.organisation.billingEmail}` : ''
        }`}
        actions={back}
      />

      <Card>
        <CardHeader>
          <CardTitle>Licences</CardTitle>
        </CardHeader>
        <CardContent>
          {data.licences.length === 0 ? (
            <p className="text-data text-ink-muted">This organisation has no licence.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-hairline">
              {data.licences.map((licence) => (
                <li key={licence.id} className="flex flex-wrap items-center gap-4 py-3">
                  <LicenceStatusChip status={licence.status} />
                  <span className="font-medium">{licence.planKey}</span>
                  <span className="text-data text-ink-muted" data-numeric>
                    {licence.slotsUsed} / {licence.slotsTotal ?? '∞'} slots
                  </span>
                  <span className="text-data text-ink-muted">
                    {licence.expiresAt ? `Expires ${formatDate(licence.expiresAt)}` : 'No expiry'}
                  </span>
                  <Button variant="secondary" size="xs" className="ml-auto" asChild>
                    <Link to="/admin/licences/$licenceId" params={{ licenceId: licence.id }}>
                      Manage
                    </Link>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Members</CardTitle>
            <CardDescription>{data.members.length} in this organisation</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col divide-y divide-hairline">
              {data.members.map((member) => (
                <li key={member.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-medium">{member.name ?? member.email}</span>
                    <span className="truncate text-micro text-ink-subtle">{member.email}</span>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <StatusChip tone={member.status === 'active' ? 'active' : 'neutral'}>
                      {member.status}
                    </StatusChip>
                    <span className="text-micro text-ink-subtle">
                      {member.lastLoginAt ? `Seen ${formatDate(member.lastLoginAt)}` : 'Never signed in'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Billing</CardTitle>
            <CardDescription>
              {data.subscriptions.length} subscription{data.subscriptions.length === 1 ? '' : 's'},{' '}
              {data.invoices.length} recent invoice{data.invoices.length === 1 ? '' : 's'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {data.subscriptions.map((subscription) => (
              <div key={subscription.id} className="flex items-center gap-3 text-data">
                <StatusChip tone={subscription.status === 'active' ? 'active' : 'pending'}>
                  {subscription.status}
                </StatusChip>
                <span className="text-ink-muted">
                  {subscription.currency} · renews {formatDate(subscription.currentPeriodEnd)}
                </span>
              </div>
            ))}
            <ul className="flex flex-col divide-y divide-hairline">
              {data.invoices.map((invoice) => (
                <li key={invoice.id} className="flex items-center justify-between py-2 text-data">
                  <span className="font-mono text-micro">{invoice.number ?? invoice.id.slice(0, 8)}</span>
                  <span data-numeric>{formatMoney(invoice.total, invoice.currency)}</span>
                  <StatusChip tone={invoice.status === 'paid' ? 'active' : 'pending'}>
                    {invoice.status}
                  </StatusChip>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Support notes</CardTitle>
            <CardDescription>Internal only. Never visible to the customer.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <form
              className="flex flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (note.trim()) addNote.mutate();
              }}
            >
              <Label htmlFor="support-note" className="sr-only">
                New support note
              </Label>
              <Textarea
                id="support-note"
                rows={3}
                maxLength={5000}
                placeholder="What did the customer ask? What did you change?"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-data text-ink-muted">
                  <Switch checked={pinned} onCheckedChange={setPinned} aria-label="Pin note" />
                  Pin to top
                </label>
                <Button type="submit" size="sm" disabled={!note.trim() || addNote.isPending}>
                  Add note
                </Button>
              </div>
            </form>
            <ul className="flex flex-col gap-3">
              {data.supportNotes.map((entry) => (
                <li key={entry.id} className="rounded-md border border-hairline bg-sunken p-3">
                  <p className="whitespace-pre-wrap text-data">{entry.body}</p>
                  <p className="mt-1.5 flex items-center gap-1.5 text-micro text-ink-subtle">
                    {entry.pinned ? <PinIcon className="size-3" aria-label="Pinned" /> : null}
                    {formatDateTime(entry.createdAt)}
                  </p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent activity</CardTitle>
            <CardDescription>Last 50 audit events for this organisation</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col divide-y divide-hairline">
              {data.recentActivity.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between gap-3 py-2 text-data">
                  <span className="truncate font-mono text-micro">{entry.action}</span>
                  <span className="shrink-0 text-micro text-ink-subtle">
                    {entry.actorLabel ?? 'system'} · {formatDateTime(entry.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/customers/$organisationId')({
  component: CustomerDetailPage,
});
